import { NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, SimpleChanges, OnChanges, LOCALE_ID, OnDestroy, input, output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AuthSevices,
  ClientService,
  CouponService,
  OmniSearchService,
  ToastMessageService,
  StaffStoreService
} from '@fitness-force/api';
import { ClientReferralStatus, ElasticProviderName } from '@fitness-force/enum';
import { AppErrors } from '@fitness-force/models';;
import { NoComponentComponent, NoOfferComponent } from '@fitness-force/grid';
import type { AddCouponWithClient, CLientReferral, ClientById, ClientCoupon, ClientCouponResponse, ClientInteraction, OfferMaster, OmniSearchResponse, STAFFINTERFACE } from '@fitness-force/models';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientInteractionTabComponent } from '@fitness-force/shared';
import { RouteStateService } from '@fitness-force/shared-utils';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';

import { DialogModule } from 'primeng/dialog';

import { PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'fitness-force-client-overview-tab',
  templateUrl: './client-overview-tab.component.html',
  styleUrls: ['./client-overview-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    ButtonModule,
    FormsModule,
    PopoverModule,
    CheckboxModule,
    SkeletonModule,
    StyleClassModule,
    ChipModule,
    DateTimeFormatPipe,
    DialogModule,
    TableModule,
    ClientInteractionTabComponent,
    AvatarModule,
    ShortNamePipe,
    TranslateModule,
    NgClass,
    TitleCasePipe
],
})
export class ClientOverviewTabComponent
  implements OnInit, OnChanges, OnDestroy
{
  readonly cd = inject(ChangeDetectorRef);
  readonly clientService = inject(ClientService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthSevices);
  private readonly couponService = inject(CouponService);
  readonly translate = inject(TranslateService);
  readonly store = inject(Store);
  readonly locale = inject(LOCALE_ID);
  private readonly omniSearchService = inject(OmniSearchService);
  private readonly routerStateService = inject(RouteStateService);
  private readonly staffStoreService = inject(StaffStoreService);

  readonly client = input<ClientById>({} as ClientById);
  readonly showSkeleton$ = input(new BehaviorSubject<boolean>(true));
  readonly clientInteractions$ = input(new BehaviorSubject<ClientInteraction[]>([]));
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly clickfromTab = input(new BehaviorSubject<boolean>(false));
  readonly clientReferrals$ = input(new BehaviorSubject<CLientReferral[]>([]));
  readonly clientOffers$ = input(new BehaviorSubject<ClientCoupon[]>([]));
  readonly height = input('44vh');
  readonly interactionId = input<number>(0);

  readonly changeTab = output<{
    changeTab: boolean;
    tabName: string;
}>();
  readonly reloadData = output<{
    relaodData: boolean;
}>();

  promotionPreferenceMap = new Map();
  showCollapsed$ = new BehaviorSubject<boolean>(true);
  showTextbox: boolean = false;
  clientNotes: string | null = '';
  // Locally tracks the note after save/delete so UI updates immediately without waiting for parent reload
  private readonly localNote = signal<string | null | undefined>(undefined);
  staffInfo: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  clientReferrals: any;
  currentUserDetails: any;
  parentClient: ClientById = {} as ClientById;
  loadingEmail: boolean = false;
  loadingSMS: boolean = false;
  loadingWhatsApp: boolean = false;
  showEligibleOffers: boolean = false;
  addOfferModal: boolean = false;
  eligibleOffersLoaded: boolean = false;

  loading = new BehaviorSubject<boolean>(false);
  column!: any;
  emptyStateComponent$ = new BehaviorSubject<any>(NoOfferComponent);
  tableData = new BehaviorSubject<OfferMaster[]>([]);
  columns: any = new BehaviorSubject([]);
  selectAll = new BehaviorSubject(false);
  referredByClient: any;
  showClientSidebar: boolean = false;
  
  // Add destroy subject for cleanup
  private readonly destroy$ = new Subject<void>();
  public noteUpdatedByStaff: STAFFINTERFACE | null = null;

  ngOnInit(): void {
    this.currentUserDetails = this.authService.currentUserDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (
      changes['client'] &&
      changes['client'].currentValue != changes['client'].previousValue
    ) {
      // Reset eligible offers cache when client changes
      this.eligibleOffersLoaded = false;
      // Clear local note override so the fresh client data is displayed
      this.localNote.set(undefined);
      this.getReferralsList();
      const client = this.client();
      if (client?.referred_by && client.referred_by != 0)
      {
        this.searchClientInomniSearch(client.referred_by.toString());
      }
      if (client?.is_child) this.getParentClient();
      
      // Load staff details for note author
      if (client?.note_updated_by) {
        this.loadNoteUpdatedByStaff(client.note_updated_by);
      }
    }
  }

  private loadNoteUpdatedByStaff(staffId: number) {
    if (staffId && staffId > 0) {
      this.staffStoreService.getStaffById(staffId)
        .pipe(
          map((staff) => staff),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (staff) => {
            this.noteUpdatedByStaff = staff;
            this.cd.detectChanges();
          },
          error: (error) => {
            console.error('Error loading staff details:', error);
            this.noteUpdatedByStaff = null;
          }
        });
    } else {
      this.noteUpdatedByStaff = null;
    }
  }

  getParentClient() {
    this.clientService
      .getClientByIdNew(this.client().parent_client_id)
      .subscribe({
        next: (res: ClientById) => {
          this.parentClient = res;
          this.cd.detectChanges();
        },
        error: (_error: any) => {
          this.cd.detectChanges();
        },
      });
  }

  getClientsEligibleOffers(toggle: boolean) {
    this.showEligibleOffers = toggle;

    // Only make API call if toggle is true and data hasn't been loaded yet
    if (toggle && !this.eligibleOffersLoaded) {
      this.clientService
        .getClientEligibleCoupons(this.tenantId(), this.clientId())
        .subscribe({
          next: (res: ClientCouponResponse) => {
            this.clientOffers$().next(res.items ? res.items : []);
            this.eligibleOffersLoaded = true;
            this.cd.detectChanges();
          },
          error: () => {
            this.clientOffers$().next([]);
            this.cd.detectChanges();
          },
        });
    }
  }

  reloadOffersList() {
    this.clientService
      .getClientEligibleCoupons(this.tenantId(), this.clientId())
      .subscribe({
        next: (res: ClientCouponResponse) => {
          this.clientOffers$().next(res.items ? res.items : []);
          this.eligibleOffersLoaded = true;
        },
        error: () => {
          this.clientOffers$().next([]);
          this.eligibleOffersLoaded = false;
        },
      });
  }

  getReferralsList() {
    this.clientService
      .getClientReferrals(this.tenantId(), this.clientId())
      .subscribe({
        next: (res: any) => {
          this.clientReferrals$().next(res.items ? res.items : []);
        },
        error: () => {
          this.clientReferrals$().next([]);
        },
      });
  }

  get showSkeleton(): boolean {
    return this.showSkeleton$().value;
  }

  /** Returns the current note — uses local override after save/delete, falls back to the input client data */
  get currentNote(): string | null {
    const local = this.localNote();
    return local !== undefined ? local : (this.client().note ?? null);
  }

  saveNotes() {
    const savedNote = this.clientNotes;
    const clientObj: Array<{ op: string; path: string; value: any }> = [];
    clientObj.push({ op: 'replace', path: '/Note', value: savedNote });
    this.clientService
      .patchClient(this.client().id, this.tenantId(), clientObj)
      .subscribe({
        next: (_data: any) => {
          this.showTextbox = false;
          // Update local note immediately so UI reflects the change without waiting for parent reload
          this.localNote.set(savedNote);
          this.reloadData.emit({ relaodData: true });
          this.translateService
            .get('CLIENT_PROFILE.NOTES_ADDED')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.showTextbox = false;
          this.toastService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  deleteNotes() {
    const clientObj: Array<{ op: string; path: string; value: any }> = [];
    clientObj.push({ op: 'replace', path: '/Note', value: null });
    this.clientService
      .patchClient(this.client().id, this.tenantId(), clientObj)
      .subscribe({
        next: () => {
          this.showTextbox = false;
          this.clientNotes = null;
          // Clear local note immediately so UI reflects the change without waiting for parent reload
          this.localNote.set(null);
          this.reloadData.emit({ relaodData: true });
          this.translateService
            .get('CLIENT_PROFILE.NOTES_DELTE')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.showTextbox = false;
          this.toastService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  changeTabClick() {
    this.changeTab.emit({ changeTab: true, tabName: 'ACTIVITY_FEED' });
  }

  async getStaffDetails(staffId: number) : Promise<any> {
    return this.staffStoreService.getStaffById(staffId).pipe(map((staff) => {
      return staff;
    }));
  }

  getOpenReferrals() {
    const data = this.clientReferrals$().value;
    return data.filter(
      (o) =>
        o.status == ClientReferralStatus.Active ||
        o.status.toString() == ClientReferralStatus[ClientReferralStatus.Active]
    );
  }

  getSuccessfullReferrals() {
    const data = this.clientReferrals$().value;
    return data.filter(
      (o) =>
        o.status == ClientReferralStatus.Successful ||
        o.status.toString() ==
          ClientReferralStatus[ClientReferralStatus.Successful]
    );
  }

  showOffer() {
    this.addOfferModal = true;
    this.getOfferList();
  }

  getOfferList() {
    this.loading.next(true);
    this.couponService.getCoupons(this.tenantId()).subscribe({
      next: (response: any) => {
        this.loading.next(false);
        let offers: OfferMaster[] = [] as OfferMaster[];
        if (response?.items) {
          offers = response.items;
          this.tableData.next(offers);
        } else {
          this.tableData.next([]);
          this.emptyStateComponent$.next(NoComponentComponent);
        }
      },
        error: (_error: any) => {
          this.tableData.next([]);
          this.loading.next(false);
        },
    });
  }

  isOfferAdded(id: number) {
    return this.clientOffers$().value.find((o) => o.id == id);
  }

  AddCouponToClient(offer: OfferMaster) {
    const clientIds: number[] = [];
    clientIds.push(this.client().id);
    const addClientToCoupon: AddCouponWithClient = {} as AddCouponWithClient;
    addClientToCoupon.client_ids = clientIds;
    this.couponService
      .AddEligibleClients(this.tenantId(), offer.id, addClientToCoupon)
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_ADDED')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          this.cd.detectChanges();
          this.eligibleOffersLoaded = false; // Reset cache when offers are modified
          this.reloadOffersList();
        },
        error: (error: any) => {
          this.toastService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  RemoveCouponToClient(offer: OfferMaster) {
    this.couponService
      .RemoveEligibleClients(this.tenantId(), offer.id, this.client().id)
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_REMOVED')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          this.cd.detectChanges();
          this.eligibleOffersLoaded = false; // Reset cache when offers are modified
          this.reloadOffersList();
        },
        error: (error: any) => {
          this.toastService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  searchClientInomniSearch(value: string) {
    const request = {
      language: this.locale,
      query: value,
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        },
      ],
    };

    this.omniSearchService.Search(request).subscribe({
      next: (response: OmniSearchResponse) => {
        const clients = response.searchResults.clients.items;
        this.referredByClient = clients.find(
          (o) => o.clt_id == this.client().referred_by
        );
        this.cd.detectChanges();
      }
    });
  }

  closeSidebar(clientId: number) {
    this.routerStateService.setClientSidebarById(clientId);
    this.routerStateService.closeClientSidebar(true);
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to unsubscribe from all observables
    this.destroy$.next();
    this.destroy$.complete();
    
    // Only complete BehaviorSubjects created in this component, not the ones passed as inputs
    if (this.showCollapsed$) {
      this.showCollapsed$.complete();
    }
    if (this.staffInfo) {
      this.staffInfo.complete();
    }
    if (this.loading) {
      this.loading.complete();
    }
    if (this.emptyStateComponent$) {
      this.emptyStateComponent$.complete();
    }
    if (this.tableData) {
      this.tableData.complete();
    }
    if (this.columns) {
      this.columns.complete();
    }
    if (this.selectAll) {
      this.selectAll.complete();
    }

    // Clear other resources
    this.promotionPreferenceMap.clear();
  }
}
